import { t } from "i18next";

import { useQueryClient } from "@tanstack/react-query";

import ViewsService from "@/services/ViewsService";

import { useModal } from "@/hooks/useModal";

import { viewIncludesProject } from "@/utils/UViews";

import { TProject, TView } from "@/types";

import { promiseToast } from "@/modules/index";

/**
 * The view mutations the context menu and the view pages share:
 * archive, delete, and adding a project created from the view's page. Each toasts and invalidates what a change to the view
 * reaches - the sidebar list, the summaries, the detail, and the
 * projects it gathers.
 *
 * A view is personal, so there is no sharing switch any more
 * (docs/modele.md, phase 6).
 *
 * Archiving confirms here (one modal for every entry point); deleting
 * stays with the caller: a menu and a page do not confirm it the same way.
 */
export const useViewActions = () => {
	const queryClient = useQueryClient();
	const { openModal } = useModal();

	const invalidate = (viewId: number) =>
		queryClient.invalidateQueries({
			predicate: (query) =>
				query.queryKey[0] === "views" ||
				query.queryKey[0] === "view" ||
				query.queryKey[0] === "analytics" ||
				(query.queryKey[0] === "projects" &&
					query.queryKey[1] === viewId),
		});

	const toggleArchived = (view: Pick<TView, "id" | "isArchived">) => {
		const prom = ViewsService.setViewArchived(
			view.id,
			!view.isArchived
		).then(() => invalidate(view.id));
		promiseToast(prom);
		return prom;
	};

	/**
	 * Archiving asks first (ArchiveConfirmModal): the promise settles once
	 * confirmed and done, never on a cancel - what a caller chains on it
	 * (leaving the view shown) only runs when it was archived.
	 * Unarchiving asks nothing.
	 */
	const archive = (
		view: Pick<TView, "id" | "isArchived" | "name">
	): Promise<void> =>
		view.isArchived
			? toggleArchived(view)
			: new Promise<void>((resolve, reject) =>
					openModal({
						type: "archiveConfirm",
						props: {
							kind: "view",
							name: view.name,
							onConfirm: () =>
								void toggleArchived(view).then(resolve, reject),
						},
					})
				);

	const remove = (view: Pick<TView, "id">) => {
		const prom = ViewsService.deleteView(view.id).then(() =>
			queryClient.invalidateQueries({
				predicate: (query) =>
					query.queryKey[0] === "views" ||
					query.queryKey[0] === "view" ||
					query.queryKey[0] === "analytics" ||
					query.queryKey[0] === "tasks" ||
					(query.queryKey[0] === "projects" &&
						query.queryKey[1] === view.id),
			})
		);
		promiseToast(prom);
		return prom;
	};

	/**
	 * A project created from the view's page joins the view, unless the
	 * view already gathers it (everything, or its client): otherwise it
	 * would not show where it was just created.
	 */
	const includeProject = (
		view: Pick<
			TView,
			"id" | "name" | "isEverything" | "projects" | "clients"
		>,
		project: Pick<TProject, "id" | "client">
	): Promise<void> => {
		if (viewIncludesProject(view, project)) return Promise.resolve();

		const prom = ViewsService.setSelection(view.id, {
			projects: [...view.projects.map((p) => p.id), project.id],
			clients: view.clients.map((c) => c.id),
		}).then(() => invalidate(view.id));
		promiseToast(prom, {
			success: t("views.projectAdded", { name: view.name }),
		});
		return prom;
	};

	return { archive, remove, includeProject };
};
